// Цели Яндекс.Метрики по навигации Контроля (business_apps#1465):
// как пользователь доходит до подъезда, дома и устройства — поиском и списком
// на главной, через флаг и дерево адресов, по хлебным крошкам, со страницы
// адреса или напрямую по ссылке.
//
// Цели (все — JavaScript-события счётчика 108469576, заводятся в настройках
// счётчика вручную):
//   control_nav_open_target — открытие целевой страницы: target, source,
//     position, page, query_len, list_size, target_uuid, sort_by,
//     filters_active, nav_type, referrer, from_route; для source home_cities —
//     locality_uuid, locality_rank, cities_count, streets_count
//   control_home_search     — поиск на главной дал результат: query_len,
//     results_count, sort_by, filters_active
//   control_home_zero_results — поиск на главной без результатов
//   control_home_flag_click — клик по флагу страны: country_uuid, countries_count
//   control_geo_step        — шаг по дереву адресов: level, position, list_size
// Новая главная за тогглом (business_apps#1486, раздел 8):
//   control_home_variant_view   — первый показ главной в визите: variant,
//     is_default, fleet_size
//   control_home_variant_switch — клик по тогглу: from, to,
//     days_since_first_seen, fleet_size
//   control_home_filter_change  — смена чипа: filter, values, values_count
//   home_variant (v1|v2) добавляется во все цели, уходящие с главной
//   (from_route = '/'), и в суточную control_active_user.
// В каждую цель добавляются session_id (см. utils/requestSession — тот же
// идентификатор уходит в Контроль-API заголовком X-Request-Session),
// company_uuid, user_id (у пользователя SSO нет uuid) и acting_company_uuid
// (сотрудник Спутника от имени партнёра).

import trackGoal from 'utils/metrika'
import Api from 'utils/api'
import {
  getRequestSessionId,
  normalizeRoute,
  currentRoute,
} from 'utils/requestSession'

export const HOME_PAGE_SIZE = 20

const HOME_ROUTE = '/'
const HOME_VARIANT_STORAGE_KEY = 'control_home_variant_last'

export const NAV_SOURCES = {
  HOME_SEARCH: 'home_search', // строка списка на главной при непустом поиске
  HOME_LIST: 'home_list', // строка первой страницы списка без поиска
  HOME_SCROLL: 'home_scroll', // строка со второй страницы и дальше
  HOME_ENTER: 'home_enter', // Enter → первый установленный
  GEO_TREE: 'geo_tree', // списки дерева: страна → … → улица → дом
  ADDRESS_PAGE: 'address_page', // списки на странице дома / подъезда
  BREADCRUMB: 'breadcrumb', // хлебные крошки в шапке
  RECENT: 'recent', // блок «Недавно установленные» на новой главной
  HOME_CITIES: 'home_cities', // блок «Мои города» на новой главной
  DIRECT_LINK: 'direct_link', // первый экран после загрузки страницы
}

const TARGET_BY_ROUTE_PREFIX = {
  c: 'country',
  r: 'region',
  a: 'area',
  l: 'locality',
  s: 'street',
  h: 'house',
  e: 'entry',
  f: 'flat',
  gate: 'gate',
  intercom: 'device',
  cameras: 'camera',
}

const UUID_REGEXP =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

const MAX_REFERRER_LENGTH = 64

let userContext = {}
let directEntryTracked = false
let homeVariant = null

const INITIAL_PATH =
  typeof window !== 'undefined' ? window.location.pathname : ''

export const setNavMetricsUser = ({ userId, companyUuid } = {}) => {
  userContext = {
    user_id: userId || null,
    company_uuid: companyUuid || null,
  }
}

// Вариант главной известен после ответа service_partners#me; последний
// известный хранится в localStorage для суточной цели control_active_user,
// которая уходит раньше — сразу после авторизации.
export const setNavMetricsHomeVariant = (variant) => {
  homeVariant = variant || null
  try {
    if (homeVariant) {
      window.localStorage.setItem(HOME_VARIANT_STORAGE_KEY, homeVariant)
    }
  } catch (error) {
    // storage недоступен
  }
}

export const getNavMetricsHomeVariant = () => {
  if (homeVariant) return homeVariant
  try {
    return window.localStorage.getItem(HOME_VARIANT_STORAGE_KEY) || null
  } catch (error) {
    return null
  }
}

// Только для тестов.
export const resetNavMetrics = () => {
  userContext = {}
  directEntryTracked = false
  homeVariant = null
}

export const targetFromPath = (pathname = '') => {
  const clean = String(pathname).split('?')[0].split('#')[0]
  const segments = clean.split('/').filter(Boolean)
  if (segments.length === 0) return 'home'
  // /index.html — start_url из manifest.json, роутер перенаправляет на главную
  if (segments.length === 1 && segments[0] === 'index.html') return 'home'
  if (segments.length < 2) return null
  return TARGET_BY_ROUTE_PREFIX[segments[0]] || null
}

export const uuidFromPath = (pathname = '') => {
  const match = String(pathname).match(UUID_REGEXP)
  return match ? match[0] : null
}

export const targetFromAddressType = (addressType) => {
  if (addressType === 'gate') return 'gate'
  if (addressType === 'house') return 'house'
  return 'entry'
}

// Источник строки списка на главной: поиск → докрутка → первая страница.
export const homeListSource = ({ queryLen = 0, page = 1 }) => {
  if (queryLen > 0) return NAV_SOURCES.HOME_SEARCH
  if (page > 1) return NAV_SOURCES.HOME_SCROLL
  return NAV_SOURCES.HOME_LIST
}

const actingCompanyUuid = () =>
  typeof Api?.getActingPartner === 'function' ? Api.getActingPartner() : null

const withContext = (payload = {}) => {
  const fromRoute =
    payload.from_route === undefined ? currentRoute() : payload.from_route
  return {
    ...payload,
    from_route: fromRoute,
    session_id: getRequestSessionId(),
    ...userContext,
    acting_company_uuid: actingCompanyUuid(),
    home_variant:
      fromRoute === HOME_ROUTE && homeVariant ? homeVariant : undefined,
  }
}

export const trackNavOpenTarget = ({
  target,
  source,
  position,
  page,
  queryLen,
  listSize,
  targetUuid,
  sortBy,
  filtersActive,
  navType,
  referrer,
  fromRoute,
  localityUuid,
  localityRank,
  citiesCount,
  streetsCount,
}) =>
  trackGoal(
    'control_nav_open_target',
    withContext({
      target,
      source,
      position,
      page,
      query_len: queryLen,
      list_size: listSize,
      target_uuid: targetUuid,
      sort_by: sortBy,
      filters_active: filtersActive,
      nav_type: navType,
      referrer,
      from_route: fromRoute,
      locality_uuid: localityUuid,
      locality_rank: localityRank,
      cities_count: citiesCount,
      streets_count: streetsCount,
    })
  )

// Строка списка «Мои устройства» на главной (клик или Enter). target и
// targetUuid можно переопределить: неустановленный домофон на новой главной
// ведёт в карточку устройства, а не на адрес.
export const trackHomeListOpen = ({
  device,
  index,
  queryLen = 0,
  sortBy,
  filtersActive,
  viaEnter = false,
  target,
  targetUuid,
}) => {
  if (!device) return false
  const position = index >= 0 ? index + 1 : undefined
  const page = position ? Math.ceil(position / HOME_PAGE_SIZE) : undefined
  return trackNavOpenTarget({
    target: target || targetFromAddressType(device.address_type),
    source: viaEnter
      ? NAV_SOURCES.HOME_ENTER
      : homeListSource({ queryLen, page }),
    position,
    page,
    queryLen,
    targetUuid: targetUuid || device.address_uuid,
    sortBy,
    filtersActive,
  })
}

// Строка блока «Недавно установленные» на новой главной.
export const trackRecentOpen = ({ device, index, listSize }) => {
  if (!device) return false
  return trackNavOpenTarget({
    target: targetFromAddressType(device.address_type),
    source: NAV_SOURCES.RECENT,
    position: index >= 0 ? index + 1 : undefined,
    listSize,
    targetUuid: device.address_uuid,
  })
}

// Город, улица или «все адреса» в блоке «Мои города» (решение 25.09: улицы
// показываем сразу и измеряем, в какой город уходят клики).
export const trackHomeCitiesOpen = ({
  target,
  targetUuid,
  position,
  listSize,
  localityUuid,
  localityRank,
  citiesCount,
  streetsCount,
}) =>
  trackNavOpenTarget({
    target,
    source: NAV_SOURCES.HOME_CITIES,
    position,
    listSize,
    targetUuid,
    localityUuid,
    localityRank,
    citiesCount,
    streetsCount,
  })

export const trackHomeSearch = ({
  queryLen,
  resultsCount,
  sortBy,
  filtersActive,
}) => {
  const payload = withContext({
    query_len: queryLen,
    results_count: resultsCount,
    sort_by: sortBy,
    filters_active: filtersActive,
  })
  const sent = trackGoal('control_home_search', payload)
  if (resultsCount === 0) {
    trackGoal('control_home_zero_results', payload)
  }
  return sent
}

export const trackHomeFlagClick = ({ countryUuid, countriesCount }) =>
  trackGoal(
    'control_home_flag_click',
    withContext({
      country_uuid: countryUuid,
      countries_count: countriesCount,
    })
  )

// Первый показ главной в визите: какой вариант увидел пользователь и выбрал ли
// он его сам (is_default — работает дефолт компании).
export const trackHomeVariantView = ({ variant, isDefault, fleetSize }) =>
  trackGoal(
    'control_home_variant_view',
    withContext({
      variant,
      is_default: Boolean(isDefault),
      fleet_size: fleetSize,
      from_route: HOME_ROUTE,
    })
  )

export const trackHomeVariantSwitch = ({
  from,
  to,
  daysSinceFirstSeen,
  fleetSize,
}) =>
  trackGoal(
    'control_home_variant_switch',
    withContext({
      from,
      to,
      days_since_first_seen: daysSinceFirstSeen,
      fleet_size: fleetSize,
      from_route: HOME_ROUTE,
    })
  )

export const trackHomeFilterChange = ({ filter, values, valuesCount }) =>
  trackGoal(
    'control_home_filter_change',
    withContext({
      filter,
      values: Array.isArray(values) ? values.join(',') : values,
      values_count:
        valuesCount === undefined && Array.isArray(values)
          ? values.length
          : valuesCount,
      from_route: HOME_ROUTE,
    })
  )

// Клик в списке дерева адресов; level — уровень, на который переходим.
export const trackGeoStep = ({ level, position, listSize, targetUuid }) =>
  trackGoal(
    'control_geo_step',
    withContext({
      level,
      position,
      list_size: listSize,
      target_uuid: targetUuid,
    })
  )

export const trackGeoTreeOpen = ({ target, position, listSize, targetUuid }) =>
  trackNavOpenTarget({
    target,
    source: NAV_SOURCES.GEO_TREE,
    position,
    listSize,
    targetUuid,
  })

export const trackAddressPageOpen = ({
  target,
  position,
  listSize,
  targetUuid,
}) =>
  trackNavOpenTarget({
    target,
    source: NAV_SOURCES.ADDRESS_PAGE,
    position,
    listSize,
    targetUuid,
  })

export const trackBreadcrumbClick = ({
  target,
  position,
  crumbsCount,
  targetUuid,
}) =>
  trackNavOpenTarget({
    target,
    source: NAV_SOURCES.BREADCRUMB,
    position,
    listSize: crumbsCount,
    targetUuid,
  })

const navigationType = () => {
  try {
    const [entry] = window.performance?.getEntriesByType?.('navigation') || []
    if (entry?.type) return entry.type // navigate | reload | back_forward
    const legacy = window.performance?.navigation?.type
    if (legacy === 1) return 'reload'
    if (legacy === 2) return 'back_forward'
    if (legacy === 0) return 'navigate'
  } catch (error) {
    // performance API недоступен
  }
  return 'unknown'
}

// none — набрали адрес / закладка; sso — вернулись с sso.sputnik.systems
// после логина; self — открыли ссылку из самого Контроля (новая вкладка);
// иначе — хост источника (мессенджер, почта, другой кабинет).
export const classifyReferrer = (referrer, ownHost) => {
  if (!referrer) return 'none'
  try {
    const host = new URL(referrer).hostname
    if (host === ownHost) return 'self'
    if (host.startsWith('sso.')) return 'sso'
    return host.slice(0, MAX_REFERRER_LENGTH)
  } catch (error) {
    return 'other'
  }
}

// Первый экран после загрузки страницы: прямой заход по ссылке, перезагрузка,
// «назад» из другого сайта. Вызывается один раз после успешной авторизации,
// поэтому SSO-круг не даёт дубля.
export const trackDirectEntry = (pathname = INITIAL_PATH) => {
  if (directEntryTracked) return false
  directEntryTracked = true

  const target = targetFromPath(pathname)
  if (!target) return false

  return trackNavOpenTarget({
    target,
    source: NAV_SOURCES.DIRECT_LINK,
    targetUuid: uuidFromPath(pathname),
    navType: navigationType(),
    referrer: classifyReferrer(
      typeof document !== 'undefined' ? document.referrer : '',
      typeof window !== 'undefined' ? window.location.hostname : ''
    ),
    fromRoute: normalizeRoute(pathname),
  })
}
